import { formatShortDate, hasProvenance, type ISODate } from '@app/shared'

import type { InterestSaveDetail } from '../../lib/data'
import { ArrowOut, SparkleSmall } from './icons'
import { QuietNote, SOURCE_DOT, SourceTile, Unavailable } from './parts'
import { detailSourceLine, displayUrl, noteEdited, savedAgo } from './labels'

// ============================================================================
// THE DETAIL PANE — 372px, right-hand side, one save fully open.
//
// It stacks four kinds of claim, and the whole design of the panel is about
// keeping them separable at a glance:
//
//   1. THE AUTHOR'S OWN WORDS   — the excerpt, quiet grey, no ornament.
//   2. WHAT A MODEL MADE OF IT  — the violet AI SUMMARY block, labelled, and it
//      cannot render without { model, sourceRef, generatedAt }. `hasProvenance`
//      is called here on the render path and not only in a test, because a
//      summary with no provenance is indistinguishable from the author's words —
//      which is the exact failure this product exists not to have.
//   3. THE OWNER'S OWN WORDS    — MY NOTE, amber, with the one rule down its
//      left edge. Never generated, never rewritten.
//   4. WHAT THE INDEX THINKS    — RELATED SAVES, and the pane says out loud that
//      they came "by meaning, not tags", because a neighbour list that looked
//      like a tag list would invite the owner to correct tags that do not exist.
//
// Absence is first-class in all four: no excerpt, no summary, no note and no
// neighbours are each a real answer, and none of them renders an empty affordance.
// ============================================================================

export function DetailPane({
  detail,
  today,
  isPending,
  error,
  onSelect,
}: {
  detail?: InterestSaveDetail | null
  today: ISODate
  isPending: boolean
  error: unknown
  onSelect: (id: string) => void
}) {
  return (
    <aside className="flex w-[372px] flex-none flex-col gap-3 overflow-hidden rounded-context border border-line bg-surface px-[18px] py-4 shadow-card">
      {error ? (
        <Unavailable error={error} className="border-0 p-0 shadow-none" />
      ) : isPending ? (
        <PaneSkeleton />
      ) : !detail ? (
        <QuietNote>
          Select a save to open it here. Nothing is chosen yet, so there is nothing to show —
          this panel never guesses which one you meant.
        </QuietNote>
      ) : (
        <Body detail={detail} today={today} onSelect={onSelect} />
      )}
    </aside>
  )
}

function PaneSkeleton() {
  return (
    <div className="flex animate-pulse flex-col gap-3" aria-hidden>
      <div className="h-5 w-2/3 rounded-full bg-fill" />
      <div className="h-[42px] rounded-row bg-fill" />
      <div className="h-[72px] rounded-row bg-line-3" />
      <div className="h-[86px] rounded-row bg-fill" />
      <span className="sr-only">Loading…</span>
    </div>
  )
}

function Body({
  detail,
  today,
  onSelect,
}: {
  detail: InterestSaveDetail
  today: ISODate
  onSelect: (id: string) => void
}) {
  const { save, related } = detail

  return (
    <>
      <header className="flex flex-none items-center gap-2">
        <SourceTile source={save.source} />
        <span className="truncate text-[11px] font-semibold leading-none text-ink-3">
          {detailSourceLine(save)}
        </span>
        <span className="ml-auto flex-none text-[10.5px] leading-none text-microcopy">
          saved {savedAgo(save.savedAt, today)}
        </span>
      </header>

      <h2 className="flex-none text-[15px] font-semibold leading-[1.35] text-pretty">
        {save.title}
      </h2>

      {save.excerpt !== null && (
        <p className="flex-none text-[12px] leading-[1.6] text-ink-3 text-pretty">{save.excerpt}</p>
      )}

      {/* The provenance gate. Not a warning and not a placeholder — absence. */}
      {hasProvenance(save.summary) && (
        <section className="flex flex-none flex-col gap-[7px] rounded-row border border-accent-line bg-accent-fill px-[13px] py-[11px]">
          <div className="flex items-center gap-[7px]">
            <SparkleSmall size={12} className="flex-none text-chat" />
            <h3 className="text-[9.5px] font-semibold uppercase leading-none tracking-[0.08em] text-accent">
              AI Summary
            </h3>
            {/* NOT IN THE MOCKUP, and deliberate: the mockup's row is empty to the
                right of the label, and the product rule above any pixel is that a
                machine-made claim can be weighed. The model and the day it read
                the save are what make that possible, at the quietest size on the
                screen. Flagged in the port report as a deviation to veto. */}
            <span className="ml-auto flex-none text-[10px] leading-none text-faintest-2">
              {save.summary.provenance.model} ·{' '}
              {formatShortDate(save.summary.provenance.generatedAt.slice(0, 10))}
            </span>
          </div>
          <p className="text-[12px] leading-[1.55] text-ink-2 text-pretty">{save.summary.text}</p>
        </section>
      )}

      {save.note !== null && (
        <section className="flex flex-none flex-col gap-[7px]">
          <div className="flex items-center gap-2">
            <h3 className="text-[9.5px] font-semibold uppercase leading-none tracking-[0.08em] text-microcopy">
              My note
            </h3>
            {save.noteEditedAt !== null && (
              <span className="text-[10px] leading-none text-faintest-2">
                {noteEdited(save.noteEditedAt, today)}
              </span>
            )}
          </div>
          <p className="rounded-row border-l-[3px] border-note-rule bg-warning-fill px-[13px] py-[11px] text-[12.5px] leading-[1.5] text-ink-2 text-pretty">
            {save.note}
          </p>
        </section>
      )}

      <section className="flex min-h-0 flex-1 flex-col gap-2">
        <div className="flex flex-none items-center gap-2">
          <h3 className="text-[9.5px] font-semibold uppercase leading-none tracking-[0.08em] text-microcopy">
            Related saves
          </h3>
          <span className="rounded-pill bg-fill-2 px-[7px] text-[10px] font-semibold leading-[1.7] text-ink-3">
            {related.length}
          </span>
          <span className="ml-auto flex-none text-[10px] leading-none text-faintest-2">
            by meaning, not tags
          </span>
        </div>

        {related.length === 0 ? (
          <QuietNote>
            {save.topic === null
              ? 'The nightly clustering has not placed this save yet, so nothing has been put near it.'
              : 'Nothing else in the library is close enough to this one to name.'}
          </QuietNote>
        ) : (
          <div className="flex flex-col gap-[6px] overflow-y-auto">
            {related.map((row) => (
              <button
                key={row.id}
                type="button"
                onClick={() => onSelect(row.id)}
                className="flex items-center gap-[9px] rounded-tile bg-surface-2 px-[11px] py-[9px] text-left transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-surface-4"
              >
                <span
                  className={`size-[5px] flex-none rounded-full ${SOURCE_DOT[row.source]}`}
                  aria-hidden
                />
                <span className="min-w-0 flex-1 text-[11.5px] font-medium leading-[1.35] text-ink-2">
                  {row.title}
                </span>
                <span className="flex-none text-[10px] leading-none text-microcopy">
                  {savedAgo(row.savedAt, today)}
                </span>
              </button>
            ))}
          </div>
        )}
      </section>

      <footer className="flex flex-none items-center gap-[9px]">
        <a
          href={save.url}
          target="_blank"
          rel="noreferrer noopener"
          className="flex flex-none items-center gap-[7px] rounded-tile bg-ink px-[13px] py-[9px] text-[11.5px] font-semibold leading-none text-surface transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-ink-2"
        >
          Open original
          <ArrowOut size={12} className="text-chat-lift" />
        </a>
        {/* Wraps rather than truncates. `displayUrl` has already thrown away the middle of the
            path; letting CSS eat the end as well would leave a locator that identifies nothing,
            which is why the mockup gives this line a 1.4 line-height in the first place. */}
        <span className="min-w-0 flex-1 break-words text-[10.5px] leading-[1.4] text-microcopy">
          {displayUrl(save.url)}
        </span>
        <button
          type="button"
          aria-disabled="true"
          title="Chat is live, but it cannot read your saves: they are not in the index it retrieves from, so an answer about this one would have nothing to cite. Indexing saves is what unblocks this — the same thing the ask bar is waiting for."
          onClick={(e) => e.preventDefault()}
          className="flex flex-none cursor-not-allowed items-center rounded-tile border border-line bg-surface px-[11px] py-2 text-[11.5px] font-semibold leading-none text-ink-2"
        >
          Ask
        </button>
      </footer>
    </>
  )
}
